// @ts-check
/**
 * If a `<section id="index">` exists, it is filled by a list of terms defined
 * (locally) by current document and a list of terms referenced (external) by
 * current document.
 */

import { addId, getIntlData, norm, xmlEscape } from "./utils.js";
import { biblio } from "./biblio.js";
import css from "../styles/dfn-index.css.js";
import { getTermFromElement } from "./xref.js";
import { html } from "./import-maps.js";
import { insertStyle } from "./insert-style.js";
import { renderInlineCitation } from "./render-biblio.js";
import { sub } from "./pubsubhub.js";
import { toCiteDetails } from "./data-cite.js";

export const name = "core/dfn-index";

const localizationStrings = {
  en: {
    heading: "Index",
    headingExternal: "Terms defined by reference",
    headingLocal: "Terms defined by this specification",
    dfnOf: "definition of",
    definesFollowing: "defines the following:",
  },
  cs: {
    heading: "Glosář",
    headingExternal: "Termíny definované odkazem",
    headingLocal: "Termíny definované touto specifikací",
    dfnOf: "definice",
    definesFollowing: "definuje následující:",
  },
  de: {
    heading: "Index",
    headingExternal: "Begriffe, die durch Verweis definiert sind",
    headingLocal: "Begriffe, die in dieser Spezifikation definiert sind",
    dfnOf: "Definition von",
    definesFollowing: "definiert Folgendes:",
  },
  es: {
    heading: "Índice",
    headingExternal: "Términos definidos por referencia",
    headingLocal: "Términos definidos por esta especificación",
    dfnOf: "definición de",
    definesFollowing: "define lo siguiente:",
  },
  ja: {
    heading: "索引",
    headingExternal: "参照によって定義された用語",
    headingLocal: "この仕様で定義された用語",
    dfnOf: "の定義",
    definesFollowing: "以下を定義します:",
  },
  ko: {
    heading: "색인",
    headingExternal: "참조로 정의된 용어",
    headingLocal: "이 명세서에서 정의된 용어",
    dfnOf: "정의",
    definesFollowing: "다음을 정의합니다:",
  },
  nl: {
    heading: "Index",
    headingExternal: "Termen gedefinieerd door verwijzing",
    headingLocal: "Termen gedefinieerd door deze specificatie",
    dfnOf: "definitie van",
    definesFollowing: "definieert het volgende:",
  },
  zh: {
    heading: "索引",
    headingExternal: "通过引用定义的术语",
    headingLocal: "由本规范定义的术语",
    dfnOf: "的定义",
    definesFollowing: "定义以下内容:",
  },
};
const l10n = getIntlData(localizationStrings);

// Terms of these _types_ are wrapped in `<code>`.
const CODE_TYPES = new Set([
  "attribute",
  "callback",
  "dict-member",
  "dictionary",
  "element-attr",
  "element",
  "enum-value",
  "enum",
  "exception",
  "extended-attribute",
  "interface",
  "method",
  "typedef",
]);

/**
 * @typedef {{ term: string, type?: string, linkFor?: string, elem: HTMLAnchorElement }} Entry
 */

export function run() {
  const index = document.querySelector("section#index");
  if (!index) {
    return;
  }

  insertStyle(css);

  index.classList.add("appendix");
  if (!index.querySelector("h2, h1")) {
    index.prepend(html`<h1>${l10n.heading}</h1>`);
  }

  const localTermIndex = html`<section id="index-defined-here">
    <h3>${l10n.headingLocal}</h3>
    ${createLocalTermIndex()}
  </section>`;
  index.append(localTermIndex);

  const externalTermIndex = html`<section id="index-defined-elsewhere">
    <h3>${l10n.headingExternal}</h3>
    ${createExternalTermIndex()}
  </section>`;
  index.append(externalTermIndex);
  for (const el of externalTermIndex.querySelectorAll(".index-term")) {
    addId(el, "index-term");
  }

  // core/dfn-index runs before core/structure (per profile order) so that
  // #index appears in the ToC. But section numbers are only assigned by
  // core/structure. We resolve this ordering dependency via the "toc" event:
  // core/structure emits it after completing section processing and numbering
  // (and any ToC generation), at which point we annotate local index entries
  // with their section numbers.
  sub("toc", appendSectionNumbers, { once: true });

  sub("beforesave", cleanup);
}

function createLocalTermIndex() {
  const dataSortedByTerm = collectLocalTerms();
  return html`<ul class="index">
    ${dataSortedByTerm.map(([term, dfns]) => renderLocalTerm(term, dfns))}
  </ul>`;
}

function collectLocalTerms() {
  /** @type {Map<string, HTMLElement[]>} */
  const data = new Map();
  /** @type {NodeListOf<HTMLElement>} */
  const elems = document.querySelectorAll("dfn:not([data-cite])");
  for (const elem of elems) {
    if (!elem.id) continue;
    const text = norm(elem.textContent);
    const elemsByTerm = data.get(text) || data.set(text, []).get(text);
    elemsByTerm?.push(elem);
  }

  const dataSortedByTerm = [...data].sort(([a], [b]) =>
    a.slice(a.search(/\w/)).localeCompare(b.slice(b.search(/\w/)))
  );

  return dataSortedByTerm;
}

/**
 * @param {string} term
 * @param {HTMLElement[]} dfns
 * @returns {HTMLLIElement}
 */
function renderLocalTerm(term, dfns) {
  /**
   * @param {HTMLElement} dfn
   * @param {string} text
   * @param {string} suffix
   */
  const renderItem = (dfn, text, suffix = "") => {
    const href = `#${dfn.id}`;
    return html`<li data-id=${dfn.id}>
      <a class="index-term" href="${href}">${{ html: text }}</a> ${
        suffix ? { html: suffix } : ""
      }
    </li>`;
  };

  if (dfns.length === 1) {
    const dfn = dfns[0];
    const type = getLocalTermType(dfn);
    const text = getLocalTermText(dfn, type, term);
    const suffix = getLocalTermSuffix(dfn, type, term);
    return renderItem(dfn, text, suffix);
  }
  return html`<li>
    ${term}
    <ul>
      ${dfns.map(dfn => {
        const type = getLocalTermType(dfn);
        const text = getLocalTermSuffix(dfn, type, term) || l10n.dfnOf;
        return renderItem(dfn, text);
      })}
    </ul>
  </li>`;
}

/** @param {HTMLElement} dfn */
function getLocalTermType(dfn) {
  const ds = dfn.dataset;
  const type = ds.dfnType || ds.idl || ds.linkType || "";
  switch (type) {
    case "":
    case "dfn":
      return "";
    default:
      return type;
  }
}

/** @param {HTMLElement} dfn */
function getLocalTermParentContext(dfn) {
  /** @type {HTMLElement | null} */
  const dfnFor = dfn.closest("[data-dfn-for]:not([data-dfn-for=''])");
  return dfnFor?.dataset.dfnFor || "";
}

/**
 * @param {HTMLElement} dfn
 * @param {string} type
 * @param {string} term
 */
function getLocalTermText(dfn, type, term) {
  let text = term;
  if (type === "enum-value") {
    text = `"${text}"`;
  }
  if (CODE_TYPES.has(type) || dfn.dataset.idl || dfn.closest("code")) {
    text = `<code>${text}</code>`;
  }
  return text;
}

/**
 * @param {HTMLElement} dfn
 * @param {string} type
 * @param {string} [term=""]
 */
function getLocalTermSuffix(dfn, type, term = "") {
  if (term.startsWith("[[")) {
    const parent = getLocalTermParentContext(dfn);
    return `internal slot for <code>${parent}</code>`;
  }

  switch (type) {
    case "dict-member":
    case "method":
    case "attribute":
    case "enum-value": {
      const typeText =
        type === "dict-member" ? "member" : type.replace("-", " ");
      const parent = getLocalTermParentContext(dfn);
      return `${typeText} for <code>${parent}</code>`;
    }
    case "interface":
    case "dictionary":
    case "enum": {
      return type;
    }
    case "constructor": {
      const parent = getLocalTermParentContext(dfn);
      return `for <code>${parent}</code>`;
    }
    default:
      return "";
  }
}

function appendSectionNumbers() {
  /**
   * @param {string} id
   */
  const getSectionNumber = id => {
    const dfn = document.getElementById(id);
    const sectionNumberEl = dfn
      ?.closest("section:not(.notoc)")
      ?.querySelector(".secno");
    if (!sectionNumberEl) {
      // dfn is in an unnumbered section (e.g. abstract, introductory) — skip
      return null;
    }
    const secNum = `§${sectionNumberEl.textContent.trim()}`;
    return html`<span class="print-only">${secNum}</span>`;
  };

  /** @type {NodeListOf<HTMLElement>} */
  const elems = document.querySelectorAll("#index-defined-here li[data-id]");
  elems.forEach(el => {
    const span = getSectionNumber(el.dataset.id ?? "");
    if (span) el.append(span);
  });
}

function createExternalTermIndex() {
  const data = collectExternalTerms();
  const dataSortedBySpec = [...data.entries()].sort(([specA], [specB]) =>
    specA.localeCompare(specB)
  );
  const indexSection = document.querySelector("section#index");
  const useFullTitle = !!indexSection?.classList.contains(
    "prefer-full-spec-title"
  );
  return html`<ul class="index">
    ${dataSortedBySpec.map(([spec, entries]) => {
      let citationElement;
      if (useFullTitle && biblio[spec]?.title) {
        citationElement = renderInlineCitation(spec, biblio[spec].title);
      } else {
        citationElement = renderInlineCitation(spec);
      }

      return html`<li data-spec="${spec}">
        ${citationElement} ${l10n.definesFollowing}
        <ul>
          ${entries
            .sort((a, b) => a.term.localeCompare(b.term))
            .map(renderExternalTermEntry)}
        </ul>
      </li>`;
    })}
  </ul>`;
}

function collectExternalTerms() {
  /** @type {Set<string>} */
  const uniqueReferences = new Set();
  /** @type {Map<string, Entry[]>} spec => entry[] */
  const data = new Map();

  /** @type {NodeListOf<HTMLAnchorElement>} */
  const elements = document.querySelectorAll(`a[data-cite]`);
  for (const elem of elements) {
    if (!elem.dataset.cite) {
      continue;
    }
    const { cite, citeFrag, xrefType, linkType, matchedText } = elem.dataset;
    // [[[SPEC#id]]] section links have both citeFrag and matchedText; skip them.
    if (citeFrag && matchedText) {
      continue;
    }
    if (!(xrefType || linkType || cite.includes("#") || citeFrag)) {
      continue;
    }
    const uniqueID = elem.href;
    if (uniqueReferences.has(uniqueID)) {
      continue;
    }

    const { linkType: type, linkFor } = elem.dataset;
    const term = getTermFromElement(elem);
    if (!term) {
      continue; // <a data-cite="SPEC"></a>
    }
    const spec = toCiteDetails(elem).key.toUpperCase();

    const entriesBySpec = data.get(spec) || data.set(spec, []).get(spec);
    entriesBySpec?.push({ term, type, linkFor, elem });
    uniqueReferences.add(uniqueID);
  }

  return data;
}

/**
 * @param {Entry} entry
 * @returns {HTMLLIElement}
 */
function renderExternalTermEntry(entry) {
  const { elem } = entry;
  const text = getTermText(entry);
  const el = html`<li>
    <span class="index-term" data-href="${elem.href}">${{ html: text }}</span>
  </li>`;
  return el;
}

// Terms of these _types_ are suffixed with their type info.
const TYPED_TYPES = new Map([
  ["attribute", "attribute"],
  ["element-attr", "attribute"],
  ["element", "element"],
  ["enum", "enum"],
  ["exception", "exception"],
  ["extended-attribute", "extended attribute"],
  ["interface", "interface"],
]);

// These _terms_ have type suffix "type".
const TYPE_TERMS = new Set([
  // Following are primitive types as per WebIDL spec:
  "boolean",
  "byte",
  "octet",
  "short",
  "unsigned short",
  "long",
  "unsigned long",
  "long long",
  "unsigned long long",
  "float",
  "unrestricted float",
  "double",
  "unrestricted double",
  // Following are not primitive types, but aren't interfaces either.
  "undefined",
  "any",
  "object",
  "symbol",
]);

/** @param {Entry} entry */
function getTermText(entry) {
  const { term, type, linkFor } = entry;
  let text = xmlEscape(term);

  if (CODE_TYPES.has(type ?? "")) {
    if (type === "extended-attribute") {
      text = `[${text}]`;
    }
    text = `<code>${text}</code>`;
  }

  const typeSuffix = TYPE_TERMS.has(term)
    ? "type"
    : TYPED_TYPES.get(type ?? "");
  if (typeSuffix) {
    text += ` ${typeSuffix}`;
  }

  if (linkFor) {
    let linkForText = linkFor;
    if (!/\s/.test(linkFor)) {
      // If linkFor is a single word, highlight it.
      linkForText = `<code>${linkForText}</code>`;
    }
    if (type === "element-attr") {
      linkForText += " element";
    }
    text += ` (for ${linkForText})`;
  }

  return text;
}

/** @param {Document} doc */
function cleanup(doc) {
  doc
    .querySelectorAll("#index-defined-elsewhere li[data-spec]")
    .forEach(el => el.removeAttribute("data-spec"));

  doc
    .querySelectorAll("#index-defined-here li[data-id]")
    .forEach(el => el.removeAttribute("data-id"));
}
